/**
 * Scoreboard - os DOIS placares do modo ONLINE num modulo so.
 *
 *   1. PLACAR AO VIVO   aparece enquanto a tecla de placar estiver pressionada
 *                       (acao 'scoreboard' do Input, hoje Tab) e so com partida
 *                       online ativa. Em campanha este arquivo nao faz nada.
 *   2. PLACAR FINAL     tela cheia com a classificacao final, ao receber
 *                       EVENTS.MATCH_END.
 *
 * Estao no MESMO modulo porque compartilham a tabela ordenada e a fonte de
 * dados: separar em dois arquivos duplicaria a montagem da tabela e a ordenacao,
 * e duas copias da mesma regra de ordenacao divergem na primeira mudanca.
 *
 * Padrao do projeto: constructor(ctx) -> async init() -> update(dt) -> dispose().
 * Nenhum sistema e importado aqui: tudo vem de `ctx` e dos eventos MATCH_JOINED,
 * MATCH_SCORE, MATCH_END e MATCH_LEFT. Nada lanca dentro do frame - o update
 * inteiro e protegido e uma falha vira UM aviso, nunca um frame perdido.
 *
 * O teclado NAO e capturado aqui de proposito: o jogador precisa continuar
 * usando Tab e Esc normalmente, entao este modulo nao registra keydown nenhum.
 * Quem le a tecla e o Input do projeto, por acao nomeada.
 *
 * ACENTUACAO: identificador e comentario ficam em ASCII, mas TEXTO QUE O JOGADOR
 * LE leva acento correto, escrito com escape unicode (\u00c7 = C-cedilha,
 * \u00d3 = O-agudo, \u00c1 = A-agudo ...), do mesmo jeito que o Menu.js.
 */

/** Acao do Input que mostra o placar ao vivo (registrada em src/core/Input.js). */
const LIVE_ACTION = 'scoreboard';

/**
 * Intervalo de redesenho do placar ao vivo, em segundos. O placar nao precisa de
 * 60 quadros por segundo: o servidor manda placar novo a cada 1s e o unico campo
 * que anda sozinho e o cronometro, que muda de segundo em segundo. 5 vezes por
 * segundo mantem o relogio honesto sem reescrever a tabela a cada frame.
 */
const LIVE_REFRESH = 0.2;

function toInt(value) {
  return Number.isFinite(value) ? Math.trunc(value) : 0;
}

/** Cronometro em M:SS - formato de placar, nao de log. */
function mmss(seconds) {
  const total = Math.max(0, Math.floor(Number.isFinite(seconds) ? seconds : 0));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s < 10 ? '0' : ''}${s}`;
}

/**
 * Ordem do placar: pontos DESC, baixas DESC, mortes ASC. E a MESMA ordem que o
 * servidor usa para decidir a colocacao - se divergir, a tela mostra um vencedor
 * diferente do que o servidor gravou no banco.
 */
function byScore(a, b) {
  if (b.score !== a.score) return b.score - a.score;
  if (b.kills !== a.kills) return b.kills - a.kills;
  return a.deaths - b.deaths;
}

/** Classe da celula pelo papel dela. Os nomes sb-* sao o contrato do netui.css. */
function classOf(role) {
  if (role === 'place') return 'sb-place net-c-pos';
  if (role === 'name') return 'sb-name net-c-name';
  return 'sb-num net-c-num';
}

const LIVE_COLUMNS = [
  { role: 'place', head: '#', value: (row, index) => String(row.place > 0 ? row.place : index + 1) },
  { role: 'name', head: 'APELIDO', value: (row) => row.nickname },
  { role: 'num', head: 'BAIXAS', value: (row) => String(row.kills) },
  { role: 'num', head: 'MORTES', value: (row) => String(row.deaths) },
  { role: 'num', head: 'PONTOS', value: (row) => String(row.score) },
  // ping 0 nao e "0 ms de latencia", e "o servidor ainda nao mediu"
  { role: 'num', head: 'PING', value: (row) => (row.ping > 0 ? `${row.ping}` : '--') }
];

const FINAL_COLUMNS = [
  { role: 'place', head: '#', value: (row, index) => String(row.place > 0 ? row.place : index + 1) },
  { role: 'name', head: 'APELIDO', value: (row) => row.nickname },
  { role: 'num', head: 'BAIXAS', value: (row) => String(row.kills) },
  { role: 'num', head: 'MORTES', value: (row) => String(row.deaths) },
  { role: 'num', head: 'PONTOS', value: (row) => String(row.score) }
];

function buildTable(columns) {
  const table = document.createElement('table');
  table.className = 'net-table';

  const thead = document.createElement('thead');
  const tr = document.createElement('tr');
  for (const col of columns) {
    const th = document.createElement('th');
    th.scope = 'col';
    th.className = classOf(col.role);
    th.textContent = col.head;
    tr.appendChild(th);
  }
  thead.appendChild(tr);
  table.appendChild(thead);

  const tbody = document.createElement('tbody');
  table.appendChild(tbody);

  return { table, tbody };
}

/**
 * Escreve as linhas REAPROVEITANDO os <tr> que ja existem. O placar ao vivo
 * redesenha varias vezes por segundo enquanto a tecla esta pressionada; recriar
 * a tabela inteira a cada passada geraria lixo e piscaria a selecao do texto.
 */
function syncRows(tbody, rows, columns, youId, meClass) {
  const trs = tbody.children;

  for (let i = 0; i < rows.length; i++) {
    let tr = trs[i];
    if (!tr) {
      tr = document.createElement('tr');
      for (const col of columns) {
        const td = document.createElement('td');
        td.className = classOf(col.role);
        tr.appendChild(td);
      }
      tbody.appendChild(tr);
    }

    const row = rows[i];
    for (let c = 0; c < columns.length; c++) {
      tr.children[c].textContent = columns[c].value(row, i);
    }
    tr.classList.toggle(meClass, youId !== null && row.id === youId);
  }

  while (trs.length > rows.length) tbody.removeChild(trs[trs.length - 1]);
}

export class Scoreboard {
  constructor(ctx) {
    this.ctx = ctx;

    // A partida online nao congela porque o jogador abriu o menu: o cronometro e
    // o placar continuam vindo do servidor, entao este modulo continua vivo na
    // pausa (o mesmo motivo do MatchState).
    this.updateWhilePaused = true;

    /** @type {Array<{id:number,nickname:string,kills:number,deaths:number,score:number,ping:number,place:number}>} */
    this._rows = [];
    /** @type {number|null} */
    this._you = null;
    // Reserva do cronometro: normalmente o tempo sai de ctx.match.timeLeft (que
    // o MatchState mantem andando entre dois placares); este campo cobre o caso
    // de o placar chegar sem o MatchState no ctx.
    this._timeLeft = 0;
    this._dirty = true;
    this._liveOpen = false;
    this._finalOpen = false;
    this._acc = 0;
    this._joining = false;
    this._offs = [];
    this._warned = new Set();

    this._build();
  }

  async init() {
    const events = this.ctx?.events;
    const EV = this.ctx?.EVENTS;
    if (!events?.on) {
      this._warnOnce('events', new Error('ctx.events indisponivel'));
      return;
    }

    if (EV?.MATCH_JOINED) {
      this._offs.push(events.on(EV.MATCH_JOINED, (p) => this._onJoined(p)));
    }
    if (EV?.MATCH_SCORE) {
      this._offs.push(events.on(EV.MATCH_SCORE, (p) => this._onScore(p)));
    }
    if (EV?.MATCH_END) {
      this._offs.push(events.on(EV.MATCH_END, (p) => this._onEnd(p)));
    }
    if (EV?.MATCH_LEFT) {
      // Saiu da partida (por escolha, por erro ou por queda): a tela final nao
      // pode ficar pousada em cima do menu.
      this._offs.push(events.on(EV.MATCH_LEFT, () => this._closeFinal()));
    }
  }

  /* ===================================================================== */
  /* laco                                                                   */
  /* ===================================================================== */

  update(dt) {
    try {
      if (!this.live) return;
      const step = Number.isFinite(dt) && dt > 0 ? dt : 0;
      const active = !!this.ctx?.match?.isActive?.();
      // isDown ja devolve false com o Input desabilitado (menu, pausa), entao o
      // placar nao aparece fora do jogo sem precisar de checagem extra aqui.
      // Com a tela final aberta o placar ao vivo tambem nao entra: ele ficaria
      // desenhado ATRAS dela, sem ninguem ver, gastando redesenho.
      const wants = active && !this._finalOpen && !!this.ctx?.input?.isDown?.(LIVE_ACTION);

      if (wants !== this._liveOpen) {
        this._liveOpen = wants;
        this.live.hidden = !wants;
        // Redesenha na abertura: o placar tem que aparecer certo no primeiro
        // frame, nao no proximo tique de LIVE_REFRESH.
        this._acc = LIVE_REFRESH;
      }

      if (!wants) return;

      this._acc += step;
      if (this._acc < LIVE_REFRESH && !this._dirty) return;
      this._acc = 0;
      this._renderLive();
    } catch (err) {
      this._warnOnce('update', err);
    }
  }

  /* ===================================================================== */
  /* eventos de partida                                                     */
  /* ===================================================================== */

  _onJoined(payload) {
    this._you = Number.isFinite(payload?.you) ? payload.you | 0 : this._readYou();
    this._setRows(payload?.players);
    // Entrou em outra partida: a tela de fim da partida anterior sai da frente.
    this._closeFinal();
  }

  _onScore(payload) {
    this._you = this._readYou();
    this._setRows(payload?.players);
    if (Number.isFinite(payload?.timeLeft)) this._timeLeft = Math.max(0, payload.timeLeft);
  }

  _onEnd(payload) {
    if (!this.final) return;
    this._you = this._readYou();
    this._setRows(payload?.standings);
    this._renderFinal();
    this._finalOpen = true;
    this.final.hidden = false;
  }

  _readYou() {
    const you = this.ctx?.match?.you;
    return Number.isFinite(you) ? you | 0 : this._you;
  }

  /**
   * Copia as linhas para objetos PROPRIOS. O MatchState reaproveita o array e os
   * objetos do placar dele (para nao gerar lixo por frame): guardar a referencia
   * faria a tela final mudar sozinha depois, junto com a partida seguinte.
   */
  _setRows(list) {
    const source = Array.isArray(list) ? list : [];
    const rows = [];

    for (let i = 0; i < source.length; i++) {
      const p = source[i];
      if (!p || !Number.isFinite(p.id)) continue;
      const id = p.id | 0;
      rows.push({
        id,
        nickname: String(p.nickname || p.name || `JOGADOR ${id}`),
        kills: toInt(p.kills),
        deaths: toInt(p.deaths),
        score: toInt(p.score),
        ping: toInt(p.ping),
        place: toInt(p.place)
      });
    }

    rows.sort(byScore);
    this._rows = rows;
    this._dirty = true;
  }

  _me() {
    if (this._you === null) return null;
    for (let i = 0; i < this._rows.length; i++) {
      if (this._rows[i].id === this._you) return { row: this._rows[i], index: i };
    }
    return null;
  }

  /* ===================================================================== */
  /* placar ao vivo                                                         */
  /* ===================================================================== */

  _renderLive() {
    const match = this.ctx?.match;
    const timeLeft = Number.isFinite(match?.timeLeft) ? match.timeLeft : (this._timeLeft || 0);
    const scoreLimit = Number.isFinite(match?.scoreLimit) ? match.scoreLimit : 0;

    this.liveRoom.textContent = String(match?.room?.name || match?.mode || 'PARTIDA').toUpperCase();
    this.liveTime.textContent = `TEMPO ${mmss(timeLeft)}`;
    this.liveLimit.textContent = scoreLimit > 0 ? `LIMITE ${scoreLimit} PTS` : 'SEM LIMITE DE PONTOS';

    syncRows(this.liveBody, this._rows, LIVE_COLUMNS, this._you, 'sb-me');
    this._dirty = false;
  }

  /* ===================================================================== */
  /* placar final                                                           */
  /* ===================================================================== */

  _renderFinal() {
    const match = this.ctx?.match;
    const me = this._me();
    const place = me ? (me.row.place > 0 ? me.row.place : me.index + 1) : 0;
    const won = place === 1;

    this.final.classList.toggle('victory', won);
    this.finalRoom.textContent = String(match?.room?.name || match?.mode || 'PARTIDA').toUpperCase();
    this.finalResult.textContent = won ? 'VIT\u00d3RIA' : 'FIM DA PARTIDA';
    this.finalScore.textContent = me ? String(me.row.score) : '--';
    this.finalPlace.textContent = place > 0 ? `#${place}` : '--';
    this.finalKills.textContent = me ? String(me.row.kills) : '--';
    this.finalDeaths.textContent = me ? String(me.row.deaths) : '--';

    syncRows(this.finalBody, this._rows, FINAL_COLUMNS, this._you, 'net-rank-me');
    this._clearFinalError();
  }

  _closeFinal() {
    this._finalOpen = false;
    if (this.final) this.final.hidden = true;
    this._clearFinalError();
  }

  async _playAgain() {
    if (this._joining) return;

    const match = this.ctx?.match;
    if (typeof match?.joinMatch !== 'function') {
      this._setFinalError('O SERVI\u00c7O DE PARTIDA N\u00c3O EST\u00c1 DISPON\u00cdVEL.');
      return;
    }

    this._joining = true;
    this._clearFinalError();
    const label = this.againButton.textContent;
    this.againButton.disabled = true;
    this.leaveButton.disabled = true;
    this.againButton.textContent = 'CONECTANDO...';

    try {
      // roomId null = o servidor escolhe a sala. Entrando de verdade, o
      // MATCH_JOINED fecha esta tela; falhando, o motivo aparece aqui mesmo.
      // Mesmo motivo do Lobby: o lock precisa sair do gesto, antes do await.
      if (!this.ctx?.input?.touchActive && !this.ctx?.touch?.supported) {
        this.ctx?.input?.requestLock?.();
      }
      const joined = await match.joinMatch(null);
      if (!joined) {
        this._setFinalError(match.lastReason || 'N\u00c3O FOI POSS\u00cdVEL ENTRAR NA PARTIDA.');
      }
    } catch (err) {
      this._setFinalError(err?.message || 'N\u00c3O FOI POSS\u00cdVEL ENTRAR NA PARTIDA.');
    } finally {
      this._joining = false;
      this.againButton.disabled = false;
      this.leaveButton.disabled = false;
      this.againButton.textContent = label;
    }
  }

  _openRanking() {
    // A tela de ranking abre POR CIMA da tela final (z-index maior) e, ao
    // fechar com Esc, o jogador volta para o placar - por isso a final fica.
    try {
      this.ctx?.events?.emit?.('ui:openRanking');
    } catch (err) {
      this._warnOnce('openRanking', err);
    }
  }

  _leave() {
    const match = this.ctx?.match;
    if (typeof match?.leaveMatch !== 'function') {
      this._closeFinal();
      return;
    }
    try {
      match.leaveMatch('menu');
    } catch (err) {
      this._warnOnce('leaveMatch', err);
      this._closeFinal();
    }
  }

  _setFinalError(message) {
    if (!this.finalError) return;
    this.finalError.textContent = String(message || '');
    this.finalError.hidden = false;
  }

  _clearFinalError() {
    if (!this.finalError) return;
    this.finalError.textContent = '';
    this.finalError.hidden = true;
  }

  /* ===================================================================== */
  /* construcao do DOM                                                      */
  /* ===================================================================== */

  _build() {
    const uiRoot = document.getElementById('ui-root') || document.body;

    /* --- placar ao vivo -------------------------------------------------- */
    const live = document.createElement('div');
    live.className = 'scoreboard scoreboard-live';
    live.hidden = true;

    const head = document.createElement('div');
    head.className = 'sb-head';
    live.appendChild(head);

    this.liveRoom = document.createElement('span');
    this.liveRoom.className = 'net-sub';
    head.appendChild(this.liveRoom);

    const badges = document.createElement('span');
    badges.className = 'net-status';
    head.appendChild(badges);

    this.liveTime = document.createElement('span');
    this.liveTime.className = 'net-badge';
    badges.appendChild(this.liveTime);

    this.liveLimit = document.createElement('span');
    this.liveLimit.className = 'net-badge';
    badges.appendChild(this.liveLimit);

    const liveTable = buildTable(LIVE_COLUMNS);
    this.liveBody = liveTable.tbody;
    live.appendChild(liveTable.table);

    uiRoot.appendChild(live);
    this.live = live;

    /* --- placar final ---------------------------------------------------- */
    const finalRoot = document.createElement('div');
    finalRoot.className = 'scoreboard scoreboard-final';
    finalRoot.hidden = true;
    finalRoot.setAttribute('role', 'dialog');
    finalRoot.setAttribute('aria-label', 'PLACAR FINAL');

    const panel = document.createElement('div');
    panel.className = 'net-panel wide';
    finalRoot.appendChild(panel);

    this.finalRoom = document.createElement('div');
    this.finalRoom.className = 'net-sub';
    panel.appendChild(this.finalRoom);

    this.finalResult = document.createElement('div');
    this.finalResult.className = 'sb-result';
    panel.appendChild(this.finalResult);

    const highlight = document.createElement('div');
    highlight.className = 'net-row';
    panel.appendChild(highlight);

    highlight.appendChild(this._bigStat('PONTUA\u00c7\u00c3O', 'finalScore', 'sb-score-big'));
    highlight.appendChild(this._bigStat('COLOCA\u00c7\u00c3O', 'finalPlace', 'sb-score-big'));
    highlight.appendChild(this._bigStat('BAIXAS', 'finalKills', 'sb-num'));
    highlight.appendChild(this._bigStat('MORTES', 'finalDeaths', 'sb-num'));

    const finalTable = buildTable(FINAL_COLUMNS);
    this.finalBody = finalTable.tbody;
    panel.appendChild(finalTable.table);

    this.finalError = document.createElement('div');
    this.finalError.className = 'net-error';
    this.finalError.setAttribute('role', 'alert');
    this.finalError.hidden = true;
    panel.appendChild(this.finalError);

    const actions = document.createElement('div');
    actions.className = 'net-actions';
    panel.appendChild(actions);

    this.againButton = this._button('JOGAR DE NOVO', 'btn primary', () => void this._playAgain());
    actions.appendChild(this.againButton);

    this.rankingButton = this._button('VER RANKING', 'btn', () => this._openRanking());
    actions.appendChild(this.rankingButton);

    this.leaveButton = this._button('SAIR', 'btn ghost', () => this._leave());
    actions.appendChild(this.leaveButton);

    uiRoot.appendChild(finalRoot);
    this.final = finalRoot;
  }

  /** Bloco rotulo + numero grande do destaque da tela final. */
  _bigStat(label, field, valueClass) {
    const box = document.createElement('div');

    const caption = document.createElement('div');
    caption.className = 'net-sub';
    caption.textContent = label;
    box.appendChild(caption);

    const value = document.createElement('div');
    value.className = valueClass;
    value.textContent = '--';
    box.appendChild(value);

    this[field] = value;
    return box;
  }

  _button(label, cls, onClick) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = cls;
    b.textContent = label;
    b.addEventListener('click', onClick);
    return b;
  }

  _warnOnce(key, err) {
    if (this._warned.has(key)) return;
    this._warned.add(key);
    console.warn(`[Scoreboard] falha em "${key}" - seguindo sem isso:`, err);
  }

  dispose() {
    for (const off of this._offs) {
      try { off?.(); } catch (err) { this._warnOnce('off', err); }
    }
    this._offs.length = 0;
    this.live?.remove?.();
    this.final?.remove?.();
    this.live = null;
    this.final = null;
    this._rows.length = 0;
  }
}
